/**
 * WebSocket Hook - PRODUCTION-GRADE
 * Hook para conexión WebSocket con actualizaciones en tiempo real
 * - Exponential backoff con máximo de reintentos
 * - Single connection guarantee (sin duplicados)
 * - Timeout de conexión
 * - Logging detallado para diagnóstico
 * - StrictMode-safe cleanup
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAgentsStore } from '@/store/agentsStore';
import type { WSMessage } from '@/types/agent';
import type { ChatMessage } from '@/types/chat';

// Check auth status from localStorage (support multiple persisted shapes)
const getAuthStatus = () => {
  try {
    const stored = localStorage.getItem('auth-storage');
    if (stored) {
      const data = JSON.parse(stored);
      // support both { status: 'authenticated' } and { state: { status: 'authenticated' } }
      if (data?.status === 'authenticated') return true;
      if (data?.state?.status === 'authenticated') return true;
      // fallback: presence of a token implies authenticated (session placeholder)
      if (data?.token) return true;
    }
  } catch {
    // ignore parse errors
  }
  return false;
};

interface UseWebSocketOptions {
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Event) => void;
  onMessage?: (message: WSMessage) => void;
}

interface ReconnectConfig {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  connectionTimeoutMs: number;
}

const DEFAULT_RECONNECT_CONFIG: ReconnectConfig = {
  maxRetries: 10, // máx 10 intentos
  initialDelayMs: 1000, // comienza con 1s
  maxDelayMs: 30000, // máx 30s
  connectionTimeoutMs: 10000, // timeout de conexión: 10s
};

// --- Shared admin WebSocket manager to avoid multiple connections during HMR/StrictMode ---
type Listener = {
  onOpen?: () => void;
  // pass CloseEvent when available so listeners can inspect code/reason
  onClose?: (ev?: CloseEvent) => void;
  onError?: (e: Event) => void;
};

export const AdminWSManager = (function () {
  let instance: ReturnType<typeof createManager> | null = null;

  function createManager() {
    let ws: WebSocket | null = null;
    let reconnectAttempts = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let stopTimer: ReturnType<typeof setTimeout> | null = null;
    let loggedSnapshot = false; // log the first admin.snapshot only once
    let connectionTimeout: ReturnType<typeof setTimeout> | null = null;
    let healthCheckTimer: ReturnType<typeof setTimeout> | null = null; // ✅ Health check timer
    let lastMessageTs = Date.now(); // ✅ Track last message received
    const listeners = new Set<Listener>();
    const messageListeners = new Set<(msg: any) => void>();
    const getBackoff = (n: number) => Math.min(1000 * Math.pow(2, n - 1), 30000);
    const STOP_DELAY_MS = 2000; // keep socket alive briefly when listeners drop to 0
    const HEALTH_CHECK_INTERVAL_MS = 30000; // ✅ 30s
    const MESSAGE_TIMEOUT_MS = 60000; // ✅ 60s sin mensajes = dead

    const start = () => {
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING))
        return;
      // If a stop was scheduled, cancel it because we're starting again
      if (stopTimer) {
        clearTimeout(stopTimer);
        stopTimer = null;
      }

      const apiBase =
        import.meta.env.VITE_API_URL ||
        `${window.location.protocol}//${window.location.host}/api/v1`;
      const wsEnv = import.meta.env.VITE_WS_URL as string | undefined;
      const wsBaseRaw = wsEnv && wsEnv.trim().length > 0 ? wsEnv.trim() : apiBase;
      const wsBase = wsBaseRaw.replace(/^http/i, 'ws').replace(/\/$/, '');
      const url = wsBase.endsWith('/api/v1') ? `${wsBase}/admin/ws` : `${wsBase}/api/v1/admin/ws`;

      ws = new WebSocket(url);

      // connection timeout
      connectionTimeout = setTimeout(() => {
        if (ws && ws.readyState === WebSocket.CONNECTING) {
          try {
            ws.close();
          } catch {}
        }
      }, DEFAULT_RECONNECT_CONFIG.connectionTimeoutMs);

      ws.onopen = () => {
        reconnectAttempts = 0;
        lastMessageTs = Date.now(); // ✅ Reset on open

        // Web Push: el WS abierto garantiza sesión válida; asegurar la suscripción
        // para recibir notificaciones de chat con la pestaña cerrada (best-effort).
        import('@/lib/push').then((m) => m.setupWebPush()).catch(() => {});
        if (connectionTimeout) {
          clearTimeout(connectionTimeout);
          connectionTimeout = null;
        }

        // ✅ Start health check.
        // Sanity guard: only run the health check if the socket is actually OPEN.
        // This protects against HMR / StrictMode weirdness where a stale timer
        // could fire after the socket has already been replaced.
        if (healthCheckTimer) {
          clearInterval(healthCheckTimer as unknown as number);
          healthCheckTimer = null;
        }
        healthCheckTimer = setInterval(() => {
          if (!ws) return; // already torn down
          if (ws.readyState !== WebSocket.OPEN && ws.readyState !== WebSocket.CONNECTING) return;

          const now = Date.now();
          const timeSinceLastMsg = now - lastMessageTs;

          if (timeSinceLastMsg > MESSAGE_TIMEOUT_MS) {
            console.warn('[AdminWS] ⚠️ No messages in 60s, connection appears dead');
            // Honor the same exponential-backoff path as onclose, instead of
            // calling `start()` synchronously. Otherwise health-check triggered
            // reconnects can thrash when many tabs hit the same dead backend.
            try {
              ws.close();
            } catch {}
            // Close triggers onclose which schedules the backoff. Do NOT call
            // start() from here — let onclose own the reconnect decision so
            // the attempt counter is incremented exactly once per failure.
            ws = null;
            if (healthCheckTimer) {
              clearInterval(healthCheckTimer as unknown as number);
              healthCheckTimer = null;
            }
          }
        }, HEALTH_CHECK_INTERVAL_MS);

        listeners.forEach((l) => l.onOpen?.());
      };

      ws.onclose = (ev) => {
        listeners.forEach((l) => l.onClose?.(ev));
        ws = null;

        // ✅ Cleanup health check timer
        if (healthCheckTimer) {
          clearInterval(healthCheckTimer);
          healthCheckTimer = null;
        }

        if (reconnectAttempts <= DEFAULT_RECONNECT_CONFIG.maxRetries) {
          const delay = getBackoff(reconnectAttempts + 1);
          reconnectAttempts += 1;
          console.log(
            `[AdminWS] 🔄 Reconnecting in ${delay}ms (attempt ${reconnectAttempts}/${DEFAULT_RECONNECT_CONFIG.maxRetries})`
          );
          reconnectTimer = setTimeout(() => start(), delay);
        } else {
          console.error('[AdminWS] ❌ Max retries reached, giving up');
        }
      };

      ws.onerror = (e) => {
        listeners.forEach((l) => l.onError?.(e));
      };

      ws.onmessage = (ev) => {
        lastMessageTs = Date.now(); // ✅ Track last message received

        try {
          const message = JSON.parse(ev.data);
          // notify external message listeners (webrtc hooks subscribe here)
          try {
            messageListeners.forEach((fn) => {
              try {
                fn(message);
              } catch {}
            });
          } catch {}
          const type = message.type;
          const data = message.data;
          const store = useAgentsStore.getState();

          switch (type) {
            case 'admin.snapshot':
              try {
                if (!loggedSnapshot) {
                  // one-time debug print to verify snapshot shape in runtime
                  console.log('[WS Manager] first admin.snapshot', data);
                  loggedSnapshot = true;
                }
              } catch (_e) {
                // ignore logging errors
              }

              // Use agents exactly as provided by the backend. The backend is the
              // source of truth for connection state; do not infer `connected`
              // client-side. This keeps UI consistent with server decisions.
              if (data?.agents && Array.isArray(data.agents)) {
                store.setAgents(
                  data.agents,
                  typeof data.lifecycleRevision === 'number' ? data.lifecycleRevision : undefined
                );
              }

              // If the snapshot contains `pending` enrollments, sync them to the
              // enrollments store so the UI shows the same pending items the server
              // knows about.
              try {
                if (
                  (data?.pending && Array.isArray(data.pending)) ||
                  (data?.rejected && Array.isArray(data.rejected))
                ) {
                  // Lazy import to avoid circular deps at module load time.
                  import('@/store/enrollmentsStore')
                    .then((m) => {
                      const state = m.useEnrollmentsStore.getState();
                      if (Array.isArray(data.pending)) state.setEnrollments(data.pending);
                      if (Array.isArray(data.rejected)) state.setRejected(data.rejected);
                    })
                    .catch(() => {
                      // ignore import failures
                    });
                }
              } catch (_e) {
                // ignore if anything unexpected happens
              }

              // Sync chat unread count from snapshot
              try {
                if (typeof data?.chatUnread === 'number') {
                  import('@/store/chatStore')
                    .then((m) => {
                      m.useChatStore.getState().setTotalUnread(data.chatUnread as number);
                    })
                    .catch(() => {});
                }
              } catch {}

              break;
            case 'agents.updated':
              // Unversioned legacy list events cannot supersede a lifecycle
              // snapshot; reconnect/refetch supplies the durable authority.
              if (Array.isArray(data)) store.setAgents(data, undefined);
              break;
            case 'enrollment.approved':
              // This is the explicit authorization boundary that can make a
              // previously revoked identity visible again. Old snapshots are
              // still ignored by the store until this event arrives.
              if (typeof data?.agentId === 'string') store.restoreAgent(data.agentId);
              break;
            case 'agent.revoked':
              if (typeof data?.agentId === 'string') {
                store.removeAgent(
                  data.agentId,
                  typeof data.lifecycleRevision === 'number' ? data.lifecycleRevision : undefined
                );
              }
              break;
            case 'agent.metrics':
              if (data?.agentId && data?.metrics)
                store.updateAgentMetrics(data.agentId, data.metrics);
              break;
            case 'agent.update.state':
              if (data?.agentId && data?.state) store.setUpdateState(data.agentId, data.state);
              break;
            case 'agent.service.event':
              if (data?.agentId) {
                store.setServiceEvent(data.agentId, data);
                if (data.status) store.updateAgentServiceStatus(data.agentId, data.status);
              }
              break;
            case 'chat.message':
              try {
                import('@/store/chatStore')
                  .then((m) => {
                    const chatMsg = data as ChatMessage;
                    if (chatMsg?.messageId) {
                      m.useChatStore.getState().appendMessage(chatMsg);
                      import('@/lib/notify')
                        .then((n) => n.notifyChatMessage(chatMsg))
                        .catch(() => {});
                    }
                  })
                  .catch(() => {});
              } catch {}
              break;
            case 'chat.conversation.updated':
              try {
                import('@/store/chatStore')
                  .then((m) => {
                    const { conversationId, status, unreadCount } = data as {
                      conversationId?: string;
                      status?: 'active' | 'archived';
                      unreadCount?: number;
                    };
                    if (!conversationId) return;
                    const chatStore = m.useChatStore.getState();
                    if (status !== undefined)
                      chatStore.updateConversationStatus(conversationId, status);
                    if (unreadCount !== undefined)
                      chatStore.updateConversationUnread(conversationId, unreadCount);
                  })
                  .catch(() => {});
              } catch {}
              break;
          }
        } catch (err) {
          console.error('[WS Manager] failed to parse message', err);
        }
      };
    };

    const stop = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (connectionTimeout) {
        clearTimeout(connectionTimeout);
        connectionTimeout = null;
      }
      if (stopTimer) {
        clearTimeout(stopTimer);
        stopTimer = null;
      }
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try {
          ws.close(1000, 'Normal disconnect');
        } catch {}
      }
      ws = null;
    };

    const scheduleStop = () => {
      if (stopTimer) return;
      stopTimer = setTimeout(() => {
        stopTimer = null;
        // only stop if still no listeners
        if (listeners.size === 0) stop();
      }, STOP_DELAY_MS);
    };

    const send = (message: unknown) => {
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(message));
          return true;
        }
      } catch (_err) {
        // swallow
      }
      return false;
    };

    const getReadyState = () => {
      // Be defensive: `WebSocket` may be unavailable in some environments
      // (or mocked), so return `null` when we cannot ascertain state.
      try {
        if (ws) return ws.readyState;
        return typeof WebSocket !== 'undefined' ? WebSocket.CLOSED : null;
      } catch {
        return null;
      }
    };

    const addListener = (l: Listener) => {
      listeners.add(l);
      // if we had scheduled a stop, cancel it because a new listener appeared
      if (stopTimer) {
        clearTimeout(stopTimer);
        stopTimer = null;
      }
    };
    const removeListener = (l: Listener) => {
      listeners.delete(l);
      if (listeners.size === 0) {
        // schedule a graceful stop to avoid churn (StrictMode/HMR)
        scheduleStop();
      }
    };
    const listenerCount = () => listeners.size;

    const addMessageListener = (fn: (msg: any) => void) => {
      messageListeners.add(fn);
    };
    const removeMessageListener = (fn: (msg: any) => void) => {
      messageListeners.delete(fn);
    };

    return {
      start,
      stop,
      addListener,
      removeListener,
      listenerCount,
      send,
      getReadyState,
      addMessageListener,
      removeMessageListener,
    } as const;
  }

  return {
    get: () => {
      if (!instance) instance = createManager();
      return instance;
    },
  };
})();

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const connectDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAuthenticatedRef = useRef(false);
  const isMountedRef = useRef(true); // track if component is mounted
  const isConnectingRef = useRef(false); // prevent duplicate connection attempts
  const reconnectAttemptsRef = useRef(0); // track retry count
  const lastCloseCodeRef = useRef<number | null>(null); // remember last close code (mutable)
  const [lastCloseCode, setLastCloseCode] = useState<number | null>(null);

  // Keep a stable ref to `options` so `connect`/`disconnect` don't
  // change identity when parent consumers pass inline callbacks.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  // Store the listener so we can reliably remove the exact same object
  // from the shared manager on disconnect.
  const listenerRef = useRef<Listener | null>(null);

  // Expose a simple connection state and optional reason for UI
  type ConnState = 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'NO_WS' | 'ERROR';
  const mapReadyToState = (raw: number | null | undefined): ConnState => {
    if (raw === 0) return 'CONNECTING';
    if (raw === 1) return 'OPEN';
    if (raw === 2) return 'CLOSING';
    if (raw === 3) return 'CLOSED';
    if (raw === null) return 'NO_WS';
    return 'NO_WS';
  };

  const mgrForInit = AdminWSManager.get();
  const [connectionState, setConnectionState] = useState<ConnState>(
    mapReadyToState(mgrForInit.getReadyState?.())
  );
  const [connectionReason, setConnectionReason] = useState<string | null>(null);
  const [lastMessageAt, setLastMessageAt] = useState<number | null>(null);

  const CONNECT_DELAY_MS = Number(import.meta.env.VITE_WS_CONNECT_DELAY_MS) || 100;

  const logWsState = useCallback((message: string, details?: Record<string, any>) => {
    const raw = AdminWSManager.get().getReadyState?.();
    const mgrReady = typeof raw === 'number' ? raw : null;
    const readyStateLabel =
      mgrReady === 0
        ? 'CONNECTING'
        : mgrReady === 1
          ? 'OPEN'
          : mgrReady === 2
            ? 'CLOSING'
            : mgrReady === 3
              ? 'CLOSED'
              : raw === null
                ? 'NO_WS'
                : 'UNKNOWN';
    const readyStateStr =
      mgrReady !== null ? `${mgrReady}(${readyStateLabel})` : `(${readyStateLabel})`;
    const listenerCount = AdminWSManager.get().listenerCount?.() ?? null;
    console.debug(`[WS] ${message}`, {
      readyState: readyStateStr,
      attempts: reconnectAttemptsRef.current,
      lastCloseCode: lastCloseCodeRef.current,
      listeners: listenerCount,
      ...details,
    });
  }, []);

  const connect = useCallback(() => {
    // Delegate to shared AdminWSManager
    const mgr = AdminWSManager.get();

    if (!isMountedRef.current) {
      logWsState('Skipping connect: component not mounted');
      return;
    }

    if (!isAuthenticatedRef.current) {
      logWsState('Skipping connect: not authenticated');
      return;
    }

    if (isConnectingRef.current) {
      logWsState('Skipping connect: connection already in progress');
      return;
    }

    isConnectingRef.current = true;
    reconnectAttemptsRef.current += 1;

    const listener: Listener = {
      onOpen: () => {
        if (!isMountedRef.current) return;
        isConnectingRef.current = false;
        reconnectAttemptsRef.current = 0;
        setConnectionState('OPEN');
        setConnectionReason(null);
        logWsState('Connected successfully');
        optionsRef.current.onOpen?.();
      },
      onClose: (ev) => {
        if (!isMountedRef.current) return;
        isConnectingRef.current = false;
        lastCloseCodeRef.current = ev?.code ?? null;
        setLastCloseCode(ev?.code ?? null);
        const reason = ev?.reason || (ev?.code ? `code:${ev.code}` : 'closed');
        setConnectionState('CLOSED');
        setConnectionReason(reason);
        logWsState('Closed by peer', { closeCode: ev?.code, reason });
        optionsRef.current.onClose?.();
      },
      onError: (e) => {
        if (!isMountedRef.current) return;
        setConnectionState('ERROR');
        setConnectionReason((e as Event).type || 'error');
        logWsState('WebSocket error', { errorType: (e as Event).type });
        optionsRef.current.onError?.(e as Event);
      },
    };

    // store and register listener
    listenerRef.current = listener;
    mgr.addListener(listener);
    mgr.start();
  }, [logWsState]);

  const disconnect = useCallback(() => {
    logWsState('Disconnecting');

    isConnectingRef.current = false;

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    // Remove shared manager listener; manager will schedule a stop if no listeners remain
    try {
      const mgr = AdminWSManager.get();
      const listener = listenerRef.current;
      if (listener) {
        mgr.removeListener(listener);
        listenerRef.current = null;
      }
    } catch (_err) {
      // ignore
    }
    if (connectDelayRef.current) {
      clearTimeout(connectDelayRef.current);
      connectDelayRef.current = null;
    }
  }, [logWsState]);

  const send = useCallback(
    (message: unknown) => {
      if (!isMountedRef.current) {
        logWsState('Skipping send: component not mounted');
        return false;
      }

      const mgr = AdminWSManager.get();
      const ok = mgr.send?.(message);
      if (!ok) logWsState('Skipping send: WebSocket not OPEN');
      return ok ?? false;
    },
    [logWsState]
  );

  // Main effect: manage WebSocket lifecycle
  useEffect(() => {
    isMountedRef.current = true;
    const isAuthenticated = getAuthStatus();
    isAuthenticatedRef.current = isAuthenticated;

    logWsState('Mount', { isAuthenticated });

    if (isAuthenticated) {
      // Reset retry counter on mount
      reconnectAttemptsRef.current = 0;
      // Schedule connect with small delay to avoid rapid mount/unmount churn
      connectDelayRef.current = setTimeout(() => {
        if (isMountedRef.current && isAuthenticatedRef.current) connect();
        connectDelayRef.current = null;
      }, CONNECT_DELAY_MS);
    } else {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      logWsState('Unmount');
      isMountedRef.current = false;
      if (connectDelayRef.current) {
        clearTimeout(connectDelayRef.current);
        connectDelayRef.current = null;
      }
      disconnect();
    };
  }, [connect, disconnect, logWsState, CONNECT_DELAY_MS]);

  // Track arrival time of last message via shared manager message listeners
  useEffect(() => {
    const mgr = AdminWSManager.get();
    const onMsg = () => {
      setLastMessageAt(Date.now());
    };
    try {
      mgr.addMessageListener?.(onMsg);
    } catch {
      // ignore if manager doesn't support it
    }
    return () => {
      try {
        mgr.removeMessageListener?.(onMsg);
      } catch {
        // ignore
      }
    };
  }, []);

  return {
    isConnected: AdminWSManager.get().getReadyState?.() === WebSocket.OPEN,
    connectionState,
    connectionReason,
    lastCloseCode,
    lastMessageAt,
    send,
    reconnect: connect,
  };
};
